import { Component, ReactNode, ErrorInfo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class OrgErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[OrgErrorBoundary] Caught error:', {
      message: error.message,
      componentStack: errorInfo.componentStack,
    });
  }

  render() {
    if (this.state.hasError) {
      const isAuthError = this.state.error?.message?.includes('401') || 
                         this.state.error?.message?.includes('Not authenticated');
      const isOrgError = this.state.error?.message?.includes('organization') ||
                        this.state.error?.message?.includes('Organization');

      return (
        <div className="flex items-center justify-center min-h-screen p-6">
          <Card className="w-full max-w-md">
            <CardContent className="p-6 text-center">
              <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-destructive" />
              <h2 className="text-xl font-semibold mb-2">
                {isAuthError ? 'Authentication Required' : 
                 isOrgError ? 'Organization Access Issue' : 
                 'Something Went Wrong'}
              </h2>
              <p className="text-muted-foreground mb-4">
                {isAuthError ? 'Your session has expired. Please log in again.' :
                 isOrgError ? 'Unable to access organization data. Please verify your organization membership.' :
                 this.state.error?.message || 'An unexpected error occurred.'}
              </p>
              <div className="flex gap-2 justify-center">
                <Button asChild variant="outline">
                  <Link to="/login">Return to Login</Link>
                </Button>
                <Button onClick={() => window.location.reload()}>
                  Retry
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
